import { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabaseClient';

interface Invoice {
  id: string;
  invoice_number: string;
  invoice_type: string;
  trade_type: string;
  subscription_period: string | null;
  amount: number;
  vat_amount: number;
  discount_amount: number;
  discount_code: string | null;
  total_amount: number;
  payment_status: string;
  payment_method: string;
  issued_date: string;
}

export default function InvoicesPanel() {
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedInvoice, setSelectedInvoice] = useState<Invoice | null>(null);

  useEffect(() => {
    fetchInvoices();
  }, []);

  const fetchInvoices = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data: profile } = await supabase
        .from('provider_profiles')
        .select('id')
        .eq('user_id', user.id)
        .single();

      if (!profile) return;

      const { data, error } = await supabase
        .from('subscription_invoices')
        .select('*')
        .eq('provider_id', profile.id)
        .order('issued_date', { ascending: false });

      if (error) throw error;
      setInvoices(data || []);
    } catch (error) {
      console.error('Error fetching invoices:', error);
    } finally {
      setLoading(false);
    }
  };

  const downloadInvoice = (invoice: Invoice) => {
    const invoiceHTML = `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="UTF-8">
        <title>Invoice ${invoice.invoice_number}</title>
        <style>
          body { font-family: Arial, sans-serif; max-width: 800px; margin: 40px auto; padding: 20px; }
          .header { text-align: center; margin-bottom: 40px; }
          .header h1 { color: #14B8A6; margin: 0; }
          .invoice-details { background: #f9fafb; padding: 20px; border-radius: 8px; margin: 20px 0; }
          .invoice-details table { width: 100%; border-collapse: collapse; }
          .invoice-details td { padding: 8px 0; }
          .invoice-details td:first-child { font-weight: bold; width: 150px; }
          .line-items { margin: 30px 0; }
          .line-items table { width: 100%; border-collapse: collapse; }
          .line-items th { background: #14B8A6; color: white; padding: 12px; text-align: left; }
          .line-items td { padding: 12px; border-bottom: 1px solid #e5e7eb; }
          .total-section { text-align: right; margin-top: 20px; }
          .total-section table { margin-left: auto; }
          .total-section td { padding: 8px 20px; }
          .total-row { font-size: 18px; font-weight: bold; color: #14B8A6; }
          .footer { text-align: center; margin-top: 40px; color: #6b7280; font-size: 14px; }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>INVOICE</h1>
          <p style="color: #6b7280; margin: 5px 0;">Taskbudy Ltd</p>
          <p style="color: #6b7280; margin: 5px 0;">Invoice #${invoice.invoice_number}</p>
          <p style="color: #6b7280; margin: 5px 0;">Company Number: 16087603</p>
          <p style="color: #6b7280; margin: 5px 0;">VAT Number: 497350946</p>
        </div>

        <div class="invoice-details">
          <table>
            <tr>
              <td>Invoice Date:</td>
              <td>${new Date(invoice.issued_date).toLocaleDateString('en-GB')}</td>
            </tr>
            <tr>
              <td>Payment Type:</td>
              <td>${invoice.invoice_type === 'subscription' ? 'Subscription Payment' : 'Workwear Purchase'}</td>
            </tr>
            ${invoice.trade_type ? `
            <tr>
              <td>Trade:</td>
              <td>${invoice.trade_type}</td>
            </tr>
            ` : ''}
            ${invoice.subscription_period ? `
            <tr>
              <td>Subscription Period:</td>
              <td>${invoice.subscription_period}</td>
            </tr>
            ` : ''}
            <tr>
              <td>Payment Status:</td>
              <td style="color: #10b981; font-weight: bold;">PAID</td>
            </tr>
            <tr>
              <td>Payment Method:</td>
              <td>${invoice.payment_method === 'stripe' ? 'Credit/Debit Card' : 'Manual Payment'}</td>
            </tr>
          </table>
        </div>

        <div class="line-items">
          <table>
            <thead>
              <tr>
                <th>Description</th>
                <th style="text-align: right;">Amount</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>${invoice.invoice_type === 'subscription' ? 'Annual Subscription' : 'Workwear Kit'}</td>
                <td style="text-align: right;">£${invoice.amount.toFixed(2)}</td>
              </tr>
              ${invoice.discount_amount > 0 ? `
              <tr>
                <td>Discount (${invoice.discount_code})</td>
                <td style="text-align: right; color: #10b981;">-£${invoice.discount_amount.toFixed(2)}</td>
              </tr>
              ` : ''}
              <tr>
                <td>VAT (20%)</td>
                <td style="text-align: right;">£${invoice.vat_amount.toFixed(2)}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <div class="total-section">
          <table>
            <tr class="total-row">
              <td>TOTAL PAID:</td>
              <td>£${invoice.total_amount.toFixed(2)}</td>
            </tr>
          </table>
        </div>

        <div class="footer">
          <p>Thank you for your payment!</p>
          <p>This is a computer-generated invoice and does not require a signature.</p>
          <p>Taskbudy Ltd</p>
          <p>Company Number: 16087603 | VAT Number: 497350946</p>
        </div>
      </body>
      </html>
    `;

    const blob = new Blob([invoiceHTML], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `Invoice-${invoice.invoice_number}.html`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-gray-900">Payment Invoices</h2>
        <div className="text-sm text-gray-600">
          Total Invoices: {invoices.length}
        </div>
      </div>

      {invoices.length === 0 ? (
        <div className="bg-white rounded-lg shadow-sm p-12 text-center">
          <i className="ri-file-list-3-line text-6xl text-gray-300 mb-4"></i>
          <h3 className="text-xl font-semibold text-gray-900 mb-2">No Invoices Yet</h3>
          <p className="text-gray-600">Your payment invoices will appear here once you make payments.</p>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Invoice Number
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Amount
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {invoices.map((invoice) => (
                  <tr key={invoice.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{invoice.invoice_number}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-600">
                        {new Date(invoice.issued_date).toLocaleDateString('en-GB')}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {invoice.invoice_type === 'subscription' ? 'Subscription' : 'Workwear'}
                      </div>
                      {invoice.trade_type && (
                        <div className="text-xs text-gray-500">{invoice.trade_type}</div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-semibold text-gray-900">
                        £{invoice.total_amount.toFixed(2)}
                      </div>
                      {invoice.discount_amount > 0 && (
                        <div className="text-xs text-green-600">
                          Saved £{invoice.discount_amount.toFixed(2)}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        <i className="ri-checkbox-circle-fill mr-1"></i>
                        Paid
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <button
                        onClick={() => setSelectedInvoice(invoice)}
                        className="text-teal-600 hover:text-teal-700 mr-3 whitespace-nowrap"
                      >
                        <i className="ri-eye-line mr-1"></i>
                        View
                      </button>
                      <button
                        onClick={() => downloadInvoice(invoice)}
                        className="text-blue-600 hover:text-blue-700 whitespace-nowrap"
                      >
                        <i className="ri-download-line mr-1"></i>
                        Download
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Invoice Detail Modal */}
      {selectedInvoice && (
        <div className="fixed inset-0 bg-black bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
              <h3 className="text-xl font-bold text-gray-900">Invoice Details</h3>
              <button
                onClick={() => setSelectedInvoice(null)}
                className="text-gray-400 hover:text-gray-600 w-8 h-8 flex items-center justify-center"
              >
                <i className="ri-close-line text-2xl"></i>
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* Invoice Header */}
              <div className="text-center pb-6 border-b border-gray-200">
                <h2 className="text-3xl font-bold text-teal-600 mb-2">INVOICE</h2>
                <p className="text-lg text-gray-600">{selectedInvoice.invoice_number}</p>
              </div>

              {/* Invoice Details */}
              <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                <div className="flex justify-between">
                  <span className="font-semibold text-gray-700">Invoice Date:</span>
                  <span className="text-gray-900">
                    {new Date(selectedInvoice.issued_date).toLocaleDateString('en-GB')}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="font-semibold text-gray-700">Payment Type:</span>
                  <span className="text-gray-900">
                    {selectedInvoice.invoice_type === 'subscription' ? 'Subscription Payment' : 'Workwear Purchase'}
                  </span>
                </div>
                {selectedInvoice.trade_type && (
                  <div className="flex justify-between">
                    <span className="font-semibold text-gray-700">Trade:</span>
                    <span className="text-gray-900">{selectedInvoice.trade_type}</span>
                  </div>
                )}
                {selectedInvoice.subscription_period && (
                  <div className="flex justify-between">
                    <span className="font-semibold text-gray-700">Period:</span>
                    <span className="text-gray-900">{selectedInvoice.subscription_period}</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="font-semibold text-gray-700">Payment Method:</span>
                  <span className="text-gray-900">
                    {selectedInvoice.payment_method === 'stripe' ? 'Credit/Debit Card' : 'Manual Payment'}
                  </span>
                </div>
              </div>

              {/* Line Items */}
              <div className="space-y-3">
                <h4 className="font-semibold text-gray-900">Payment Breakdown</h4>
                <div className="space-y-2">
                  <div className="flex justify-between py-2 border-b border-gray-200">
                    <span className="text-gray-700">
                      {selectedInvoice.invoice_type === 'subscription' ? 'Annual Subscription' : 'Workwear Kit'}
                    </span>
                    <span className="font-medium text-gray-900">£{selectedInvoice.amount.toFixed(2)}</span>
                  </div>
                  {selectedInvoice.discount_amount > 0 && (
                    <div className="flex justify-between py-2 border-b border-gray-200">
                      <span className="text-green-600">
                        Discount ({selectedInvoice.discount_code})
                      </span>
                      <span className="font-medium text-green-600">
                        -£{selectedInvoice.discount_amount.toFixed(2)}
                      </span>
                    </div>
                  )}
                  <div className="flex justify-between py-2 border-b border-gray-200">
                    <span className="text-gray-700">VAT (20%)</span>
                    <span className="font-medium text-gray-900">£{selectedInvoice.vat_amount.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between py-3 bg-teal-50 px-4 rounded-lg">
                    <span className="text-lg font-bold text-gray-900">Total Paid</span>
                    <span className="text-lg font-bold text-teal-600">
                      £{selectedInvoice.total_amount.toFixed(2)}
                    </span>
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-3 pt-4">
                <button
                  onClick={() => downloadInvoice(selectedInvoice)}
                  className="flex-1 bg-teal-600 text-white px-6 py-3 rounded-lg hover:bg-teal-700 transition-colors font-medium whitespace-nowrap"
                >
                  <i className="ri-download-line mr-2"></i>
                  Download Invoice
                </button>
                <button
                  onClick={() => setSelectedInvoice(null)}
                  className="px-6 py-3 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors font-medium whitespace-nowrap"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
